import React, { useCallback, useMemo, useState } from 'react'
import { useDebouncedCallback } from 'use-debounce'
import _ from 'lodash'

import ConsultationInfoComponent from '@/pages/diagnostic/components/ConsultationInfo'
import { SpecialityAutocompleteOption } from '@/components/ui/autocomplete/SpecialitiesAutoComplete'
import { consultationOfflineStore, OfflineConsultation } from '@/core/offline'

type OfflineConsultationInfoProps = {
    consultation: OfflineConsultation
}

export function ConsultationInfo(props: OfflineConsultationInfoProps) {
    const { consultation } = props

    // For offline mode, we'll use static data since we can't fetch from the server
    const reasons = useMemo(() => [
        'Consultation générale',
        'Contrôle médical',
        'Urgence',
        'Suivi médical',
        'Consultation spécialisée'
    ], [])

    const specialities = useMemo(() => [
        { _id: '1', name: 'Médecine générale', code: 'MG' },
        { _id: '2', name: 'Cardiologie', code: 'CARDIO' },
        { _id: '3', name: 'Dermatologie', code: 'DERMATO' },
        { _id: '4', name: 'Pédiatrie', code: 'PEDIATRIE' },
        { _id: '5', name: 'Neurologie', code: 'NEURO' },
    ] as SpecialityAutocompleteOption[], [])

    const [selectedSpeciality, setSelectedSpeciality] = useState<SpecialityAutocompleteOption | null>(() => {
        if (consultation.speciality) {
            return specialities.find(s => s.name === consultation.speciality) || null
        }
        return null
    })

    const [reason, setReason] = useState<string>(consultation?.reason || '')

    const handleConsultationUpdate = useDebouncedCallback(async (values: { reason?: string; speciality?: string }) => {
        try {
            const updatedConsultation = {
                ...consultation,
                ...values,
                _lastModified: Date.now(),
                _syncStatus: 'pending' as const,
            }
            await consultationOfflineStore.update(updatedConsultation)
        } catch (error) {
            console.error('Error updating consultation:', error)
        }
    }, 1000)

    const handleReasonChange = useCallback((newReason: string) => {
        setReason(newReason)
        handleConsultationUpdate({ reason: newReason })
    }, [handleConsultationUpdate])

    const handleSpecialityChange = (
        event: any,
        newValue: (string | SpecialityAutocompleteOption)[] | SpecialityAutocompleteOption | string | null,
        typeOfEvent: string
    ) => {
        if (Array.isArray(newValue)) {
            return
        }
        if (typeOfEvent === 'removeOption' || typeOfEvent === 'clear') {
            setSelectedSpeciality(null)
            handleConsultationUpdate({ speciality: '' })
            return
        }
        if (typeOfEvent === 'createOption') {
            // In offline mode, we can create a new speciality option locally
            if (typeof newValue === 'string') {
                const newSpeciality: SpecialityAutocompleteOption = {
                    _id: `offline_${Date.now()}`,
                    name: newValue,
                    code: 'CREATED.' + newValue.toLowerCase().replace(/ /g, '-'),
                }
                setSelectedSpeciality(newSpeciality)
                handleConsultationUpdate({ speciality: newValue })
            }
            return
        }
        if (typeOfEvent === 'selectOption') {
            if (typeof newValue === 'string' || !newValue) {
                return
            }
            if (newValue.isNew && newValue.inputValue) {
                const newSpeciality: SpecialityAutocompleteOption = {
                    _id: `offline_${Date.now()}`,
                    name: newValue.inputValue,
                    code: 'CREATED.' + newValue.inputValue.toLowerCase().replace(/ /g, '-'),
                }
                setSelectedSpeciality(newSpeciality)
                handleConsultationUpdate({ speciality: newValue.inputValue })
                return
            }
        }
        if (typeof newValue === 'string' || !newValue) {
            return
        }
        setSelectedSpeciality(newValue)
        handleConsultationUpdate({ speciality: newValue.name })
    }

    const handleInputChange = useDebouncedCallback((newInputValue: string) => {
        // In offline mode, we can't search for specialities from the server
        // This would typically filter the local specialities list
    }, 500)

    return (
        <ConsultationInfoComponent
            selectedSpeciality={selectedSpeciality}
            specialities={specialities}
            onInputChange={handleInputChange}
            onSpecialityChange={handleSpecialityChange}
            reason={reason}
            reasons={reasons}
            onReasonChange={handleReasonChange}
        />
    )
}

export default ConsultationInfo 